import React, { useEffect, useState } from 'react';
import { deleteMessageForUser, showMessagingSent } from '../../../services/admin/Messaging.tsx';
import { Messaging } from '../../../models/Messaging.tsx';
import { TbMessages } from "react-icons/tb";
import { useUser } from '../../../context/UserContext.tsx';
import { FaSyncAlt } from 'react-icons/fa';
import { getRecipientSummary } from '../../../helpers/messagingRecipients.ts';
import RecipientsPanel from './RecipientsPanel.tsx';
import '../../../styles/Messaging.css';
import {
    formatAdminFiguresDateTime,
    formatAdminFiguresTime,
    isSameAdminFiguresCalendarDay,
} from '../../../helpers/adminFiguresTime.tsx';

const formatMessageDate = (seconds: number) => {
    const date = new Date(seconds * 1000);
    if (isSameAdminFiguresCalendarDay(date, new Date())) {
        return formatAdminFiguresTime(date);
    }
    return formatAdminFiguresDateTime(date, {
        month: 'short',
        day: 'numeric',
        year: undefined,
        hour: '2-digit',
        minute: '2-digit',
    });
};

const SentMessaging = () => {

    const { user: currentUser } = useUser();
    const [messages, setMessages] = useState<Messaging[]>([]);
    const [expandedMessages, setExpandedMessages] = useState<Record<string, boolean>>({});
    const [selectedMessages, setSelectedMessages] = useState<Set<string>>(new Set());
    const [isLoading, setIsLoading] = useState(false);

    const fetchMessages = async () => {
        if (!currentUser?.userName) return;
        setIsLoading(true);
        try {
            const fetchedMessages = await showMessagingSent(currentUser.userName);
            setMessages(fetchedMessages);
        } catch (error) {
            console.error("Error fetching messages: ", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchMessages();
    }, [currentUser?.userName]);

    const toggleMessage = (messageId: string) => {
        setExpandedMessages(prev => ({
            ...prev,
            [messageId]: !prev[messageId],
        }));
    };

    const handleCheckboxChange = (messageId: string) => {
        setSelectedMessages(prevSelected => {
            const newSelected = new Set(prevSelected);
            if (newSelected.has(messageId)) {
                newSelected.delete(messageId);
            } else {
                newSelected.add(messageId);
            }
            return newSelected;
        });
    };

    const handleDeleteSelected = async () => {
        if (!currentUser?.userName || selectedMessages.size === 0) return;
        try {
            const deletePromises = Array.from(selectedMessages).map(messageId =>
                deleteMessageForUser(messageId, currentUser.userName)
            );
            await Promise.all(deletePromises);
            setMessages(prevMessages => prevMessages.filter(message => !selectedMessages.has(message.id || '')));
            setSelectedMessages(new Set());
        } catch (error) {
            console.error("Error deleting selected messages: ", error);
        }
    };

    if (!currentUser) return <div>Loading...</div>;

    if (isLoading) {
        return (
            <div className="loader-container">
                <FaSyncAlt className="loader-icon-proyect" />
            </div>
        );
    }

    const sortedMessages = messages
        .filter(message => !message.deletedBy?.includes(currentUser.userName))
        .sort((a, b) => (b.createDate.seconds - a.createDate.seconds));

    return (
        <div className='mt-2 container-messag'>
            <div className="inbox-header">
                <span>MY SENT MESSAGES</span>
                <button
                    className='btn btn-outline-danger btn-sm'
                    onClick={handleDeleteSelected}
                    disabled={selectedMessages.size === 0}
                >
                    Delete Selected
                </button>
            </div>
            <div className="line-block-messag"></div>
            <div className="message-area">
                {sortedMessages.length === 0 ? (
                    <div className="messaging-empty-state">
                        <TbMessages />
                        <p>You haven't sent any messages yet</p>
                    </div>
                ) : (
                    sortedMessages.map((message) => {
                        const recipients = message.recipientUsers ?? [];
                        const recipientSummary = getRecipientSummary(recipients, message.recipientLabel);
                        const showRecipientDetail = recipients.length > 1;

                        return (
                            <div key={message.id} className='message-item'>
                                <div
                                    className="message-header"
                                    onClick={() => toggleMessage(message.id || '')}
                                >
                                    <div className="message-header-info">
                                        <input
                                            id={`checkbox-${message.id}`}
                                            type="checkbox"
                                            checked={selectedMessages.has(message.id || '')}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={() => handleCheckboxChange(message.id || '')}
                                        />
                                        <div className="message-avatar">→</div>
                                        <div className="message-sender-block">
                                            <div className="message-subject">{message.subject}</div>
                                            <div className="message-sender">To: {recipientSummary}</div>
                                        </div>
                                    </div>
                                    <span className='message-date'>
                                        {formatMessageDate(message.createDate.seconds)}
                                    </span>
                                </div>
                                {expandedMessages[message.id || ''] && (
                                    <div className="message-content">
                                        <div className="line-block-messag2"></div>
                                        <span className='my-inbox-content'>{message.messageBody}</span>
                                        {showRecipientDetail && (
                                            <div className="message-recipients-detail">
                                                <div className="message-recipients-detail-label">Recipients</div>
                                                <RecipientsPanel
                                                    recipients={recipients}
                                                    groupLabel={message.recipientLabel}
                                                    readOnly
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default SentMessaging;
